import { FunctionComponent, useState, useMemo } from 'react';
import Image from 'next-export-optimize-images/image';
import TooltipV2 from '~/components/shared/TooltipV2';
import classNames from 'classnames';
import useElementClicked from '~/lib/segment/useElementClicked';
import { useVariantIds } from '~/lib/hooks/useVariantIds';

// Container classes
// Layout classes
const containerClasses =
  'flex flex-col md:flex-row justify-center items-center';
const cardGroupClasses = 'flex gap-6 justify-center relative';
const dividerClasses =
  'w-[160px] md:w-[2px] h-[2px] md:h-[160px] bg-black my-4 md:mx-6 md:my-8';

// Card classes
const cardWrapperClasses = 'relative w-[160px] h-[220px]';
const cardSideClasses =
  'absolute inset-0 w-full h-full rounded-lg transition-all duration-300 overflow-visible';

const cardContentClasses = 'relative w-full h-full overflow-visible';

// Content classes
const contentWrapperClasses = 'flex flex-col items-center h-full';
const contentContainerClasses =
  'flex-1 flex flex-col items-center pt-4 pb-16 justify-center';
const backContentContainerClasses =
  'flex-1 flex flex-col items-center justify-center px-6 pb-4';

// Icon classes
const circleIconWrapperClasses = 'absolute top-4 right-4 w-3 h-3';
const mainIconContainerClasses = 'w-6 h-6';

// Typography classes
const priceContainerClasses = 'flex items-baseline justify-center';

// Button classes
const buttonContainerClasses =
  'absolute bottom-4 left-0 right-0 z-10 flex justify-center transition-all duration-300';
const buttonArrowClasses = 'ml-1';

// Tooltip classes
const tooltipContainerClasses =
  'absolute bottom-[68px] left-0 right-0 z-20 flex justify-center transition-opacity duration-300';

// Badge classes
const badgeWrapperClasses = 'absolute -top-4 left-0 right-0 z-10';

interface MembershipCard {
  type: string;
  price: string;
  period: string;
  discount?: string;
  icon: string;
  pillText?: string;
  isPopular?: boolean;
  disclaimer?: string;
  disclaimerLink?: string;
  frontContent: {
    title: string;
    description: string;
    frontFlipText: string;
  };
  backContent: {
    title: string;
    description: string;
    backFlipText: string;
  };
}

interface MembershipCardsProps {
  cards: MembershipCard[];
  onSelect: (index: number) => void;
  variant?: 'default' | 'holiday';
}

const MembershipCards: FunctionComponent<MembershipCardsProps> = ({
  cards,
  onSelect,
  variant,
}) => {
  const [activeCard, setActiveCard] = useState(0);
  const [flippedCards, setFlippedCards] = useState<boolean[]>(
    new Array(cards.length).fill(false)
  );
  const { isDarkMode } = useVariantIds();

  // Call hooks at the top level for each possible card
  // We need to call hooks for the maximum number of cards that could exist
  const cardClickTracker0 = useElementClicked(
    'CARD_CLICK_1',
    'MEMBERSHIP_TYPE',
    'BUTTON'
  );
  const cardClickTracker1 = useElementClicked(
    'CARD_CLICK_2',
    'MEMBERSHIP_TYPE',
    'BUTTON'
  );
  const cardClickTracker2 = useElementClicked(
    'CARD_CLICK_3',
    'MEMBERSHIP_TYPE',
    'BUTTON'
  );
  const cardClickTracker3 = useElementClicked(
    'CARD_CLICK_4',
    'MEMBERSHIP_TYPE',
    'BUTTON'
  );

  const cardFlipTracker0 = useElementClicked(
    'CARD_FLIP_1',
    'MEMBERSHIP_TYPE',
    'BUTTON'
  );
  const cardFlipTracker1 = useElementClicked(
    'CARD_FLIP_2',
    'MEMBERSHIP_TYPE',
    'BUTTON'
  );
  const cardFlipTracker2 = useElementClicked(
    'CARD_FLIP_3',
    'MEMBERSHIP_TYPE',
    'BUTTON'
  );
  const cardFlipTracker3 = useElementClicked(
    'CARD_FLIP_4',
    'MEMBERSHIP_TYPE',
    'BUTTON'
  );

  // Organize trackers into arrays for easy access
  const cardClickTrackers = useMemo(
    () =>
      [
        cardClickTracker0,
        cardClickTracker1,
        cardClickTracker2,
        cardClickTracker3,
      ].slice(0, cards.length),
    [
      cardClickTracker0,
      cardClickTracker1,
      cardClickTracker2,
      cardClickTracker3,
      cards.length,
    ]
  );

  const cardFlipTrackers = useMemo(
    () =>
      [
        cardFlipTracker0,
        cardFlipTracker1,
        cardFlipTracker2,
        cardFlipTracker3,
      ].slice(0, cards.length),
    [
      cardFlipTracker0,
      cardFlipTracker1,
      cardFlipTracker2,
      cardFlipTracker3,
      cards.length,
    ]
  );

  const handleCardClick =
    (index: number) => (event: React.MouseEvent<HTMLElement>) => {
      setActiveCard(index);
      onSelect(index);
      cardClickTrackers[index](event.currentTarget);
    };

  const handleLearnMoreClick =
    (index: number) => (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      setFlippedCards((prev) => {
        const newState = [...prev];
        newState[index] = !newState[index];
        return newState;
      });
      cardFlipTrackers[index](event.currentTarget);
    };

  const imgPath = `${process.env.domainPath}/images/sections/hero/`;

  const badgeClasses = classNames(
    'text-white text-sm py-1 px-4 rounded-full w-fit mx-auto',
    isDarkMode ? 'bg-digital-walmart-blue' : 'bg-ozark-noir'
  );
  const getCardContainerClasses = (isActive: boolean) =>
    classNames(
      'absolute inset-0 rounded-lg cursor-pointer transition-all duration-300',
      isDarkMode
        ? isActive
          ? 'ring-2 ring-gray-200'
          : 'ring-1 ring-ozark-noir'
        : isActive
        ? `ring-2 ${
            variant === 'holiday' ? 'ring-secondary-dark' : 'ring-ozark-noir'
          }`
        : 'ring-1 ring-gray-200 hover:ring-gray-300'
    );

  //Typography classes
  const getFrontCardClasses = (isFirstGroup: boolean, isFlipped: boolean) =>
    classNames(
      cardSideClasses,
      isDarkMode
        ? 'bg-secondary-dark'
        : isFirstGroup
        ? 'bg-[#E9F1FE]'
        : 'bg-white',
      isFlipped
        ? 'opacity-0 scale-95 pointer-events-none'
        : 'opacity-100 scale-100 pointer-events-auto'
    );
  const getBackCardClasses = (isFirstGroup: boolean, isFlipped: boolean) =>
    classNames(
      cardSideClasses,
      isDarkMode
        ? 'bg-secondary-dark'
        : isFirstGroup
        ? 'bg-[#E9F1FE]'
        : 'bg-white',
      isFlipped
        ? 'opacity-100 scale-100 pointer-events-auto'
        : 'opacity-0 scale-95 pointer-events-none'
    );
  const titleClasses = classNames(
    'text-3xl font-semibold text-digital-walmart-blue',
    {
      'text-white': isDarkMode,
    }
  );
  const backTitleClasses = classNames(
    'text-base font-semibold mb-2 text-digital-walmart-blue',
    {
      'text-white': isDarkMode,
    }
  );
  const priceBaseClasses = classNames('text-base', {
    'text-white': isDarkMode,
  });
  const priceValueClasses = classNames('text-xl', {
    'text-white': isDarkMode,
  });
  const priceDecimalClasses = classNames('text-xs', {
    'text-white': isDarkMode,
  });
  const pricePeriodClasses = classNames('text-sm text-gray-600', {
    'text-white': isDarkMode,
  });
  const backDescriptionClasses = classNames(
    'text-sm text-center text-gray-600',
    {
      'text-white': isDarkMode,
    }
  );
  const discountContainerClasses = classNames(
    'flex items-center gap-1 text-sm relative text-digital-walmart-blue',
    {
      'text-spark-yellow': isDarkMode,
    }
  );
  const buttonClasses = classNames(
    'text-sm font-medium hover:underline flex items-center text-digital-walmart-blue',
    {
      'text-white': isDarkMode,
    }
  );

  const frontCardClassesForIndex = (idx: number) =>
    getFrontCardClasses(idx < 2, flippedCards[idx]);
  const backCardClassesForIndex = (idx: number) =>
    getBackCardClasses(idx < 2, flippedCards[idx]);
  const cardContainerClassesForIndex = (idx: number) =>
    getCardContainerClasses(activeCard === idx);

  const renderCard = (card: MembershipCard, index: number) => (
    <div key={card.type} className={cardWrapperClasses}>
      <button
        onClick={handleCardClick(index)}
        className={cardContainerClassesForIndex(index)}
        type='button'
      >
        <div className={cardContentClasses}>
          {/* Front side */}
          <div className={frontCardClassesForIndex(index)}>
            <div className={circleIconWrapperClasses} style={{ zIndex: 2 }}>
              <Image
                src={`${imgPath}${
                  isDarkMode
                    ? activeCard === index
                      ? 'yellow-circle-check.svg'
                      : 'yellow-circle-icon.svg'
                    : activeCard === index
                    ? 'blue-circle-check.svg'
                    : 'circle.svg'
                }`}
                alt={activeCard === index ? 'Selected' : 'Not selected'}
                width={16}
                height={16}
              />
            </div>
            <div className={contentWrapperClasses}>
              <div className={contentContainerClasses}>
                <div className={mainIconContainerClasses}>
                  <Image
                    src={`${imgPath}${
                      isDarkMode ? `white-${card.icon}` : card.icon
                    }`}
                    alt={`${card.type} icon`}
                    width={24}
                    height={24}
                  />
                </div>

                <h3 className={titleClasses}>{card.frontContent.title}</h3>

                <div className={priceContainerClasses}>
                  <span className={priceBaseClasses}>$</span>
                  <span className={priceValueClasses}>
                    {card.price.split('.')[0]}
                    {card.price.includes('.') && (
                      <sup className={priceDecimalClasses}>
                        .{card.price.split('.')[1]}
                      </sup>
                    )}
                  </span>
                  <span className={pricePeriodClasses}>/{card.period}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Back side */}
          <div className={backCardClassesForIndex(index)}>
            <div className={circleIconWrapperClasses} style={{ zIndex: 2 }}>
              <Image
                src={`${imgPath}${
                  isDarkMode
                    ? activeCard === index
                      ? 'yellow-circle-check.svg'
                      : 'yellow-circle-icon.svg'
                    : activeCard === index
                    ? 'blue-circle-check.svg'
                    : 'circle.svg'
                }`}
                alt={activeCard === index ? 'Selected' : 'Not selected'}
                width={16}
                height={16}
              />
            </div>
            <div className={contentWrapperClasses}>
              <div className={backContentContainerClasses}>
                <p className={backTitleClasses}>{card.backContent.title}</p>
                <p className={backDescriptionClasses}>
                  {card.backContent.description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </button>

      {card.isPopular && (
        <div className={badgeWrapperClasses} aria-label='Most Popular'>
          <div className={badgeClasses}>{card.pillText}</div>
        </div>
      )}

      {/* Tooltip container */}
      <div
        className={`${tooltipContainerClasses} ${
          !flippedCards[index] ? 'opacity-100' : 'opacity-0 invisible'
        }`}
      >
        {card.discount && card.disclaimer && (
          <>
            {card.discount && (
              <div className={`${discountContainerClasses} `}>
                <span>{card.discount}</span>
              </div>
            )}
            <TooltipV2
              text={card.disclaimer || ''}
              link={card.disclaimerLink || ''}
              elementPosition={`Card-Tooltip-${index}`}
              className={`inline-flex ${
                isDarkMode ? 'fill-spark-yellow' : 'fill-digital-walmart-blue'
              }`}
            />
          </>
        )}
      </div>

      {/* Moved button outside */}
      <div className={buttonContainerClasses}>
        <button
          onClick={handleLearnMoreClick(index)}
          className={`${buttonClasses} cursor-pointer transition-all duration-300`}
        >
          {flippedCards[index]
            ? card.backContent.backFlipText
            : card.frontContent.frontFlipText}
          <span className={buttonArrowClasses}>→</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className={containerClasses}>
      <div className={cardGroupClasses}>
        {cards.slice(0, 2).map((card, index) => renderCard(card, index))}
      </div>
      <div className={dividerClasses}></div>
      <div className={cardGroupClasses}>
        {cards.slice(2).map((card, index) => renderCard(card, index + 2))}
      </div>
    </div>
  );
};

export default MembershipCards;
